import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { type EntitySuggestion, type System } from '@bliks-io/contracts';
import {
  Badge,
  EntityPickerHighlightedText,
  SystemCard,
  type EntityPickerConfig,
  type EntityPickerSearchKey,
  type EntityPickerSuggestNew
} from '@bliks-io/ui';
import { ENTITY_ICONS } from '@/constants/entityIcons';
import { useSystems } from '@/api/queries/useSystems';
import { useSystemPickerSuggestions } from '@/api/queries/usePickerSuggestions';
import { usePermissions } from '@/hooks/usePermissions';
import { SystemCreateDialog } from '../SystemCreateDialog';
import { SystemSuggestionDialog } from '../SystemSuggestionDialog';
import { buildEntityPickerCopy } from '../buildCopy';
import { buildRowMetaIndex, shouldShowBucketView } from '../shared/buildBucketSections';
import { buildPendingSystemItems } from '../shared/pendingSuggestionUtils';
import { SystemPickerBuckets } from '../system-picker/SystemPickerBuckets';
import {
  usePickerSlotSuggestions,
  type PickerSlotMode,
  type UsePickerSlotSuggestionsResult
} from './usePickerSlotSuggestions';

const SEARCH_KEYS: EntityPickerSearchKey<System>[] = [
  { name: 'name', weight: 0.6 },
  { name: 'description', weight: 0.2 },
  { name: 'category', weight: 0.2 }
];

export interface SystemPickerRequestContext {
  sourceStepRecordingId: string;
  sourceStepRecordingPlace: 'systems_used' | 'successor_system';
  sourceRuleId?: string;
}

export interface UseSystemPickerConfigArgs {
  open: boolean;
  mode: 'single' | 'multi';
  processId?: string;
  recordingId?: string;
  /**
   * Start event node anchor (issue 11): suggestions created here anchor
   * `sourceRecordingId` to this node. Also supplied by node-anchored flows
   * without a recording of their own (resolve-unclear).
   */
  startEventNodeId?: string;
  /**
   * Draft-editor host: create/link post to the draft suggestion routes under
   * this process. The hosts null `recordingId` out when this is set — a draft
   * node is not a recording, so the recommendation query is keyed on the
   * process alone and the draft node reaches the suggestion as
   * `startEventNodeId` / `requestContext.sourceStepRecordingId`.
   */
  draftProcessId?: string;
  requestContext?: SystemPickerRequestContext;
  selectedIds: string[];
  /** Read-only pending suggestions to show as a ghost section. */
  pendingSuggestions?: EntitySuggestion[];
  /**
   * Host opt-in for direct creation (editor only). With the create
   * permission it wins over a source node: the picker creates instead of
   * suggesting.
   */
  allowCreate?: boolean;
}

export interface UseSystemPickerConfigResult {
  items: System[];
  isLoading: boolean;
  mergedSelectedIds: string[];
  config: EntityPickerConfig<System>;
  suggestNew?: EntityPickerSuggestNew;
  /** Tells the consumer whether `onUseExistingEntity` carries a pick or a create. */
  slotMode: PickerSlotMode;
  renderSuggestDialog: UsePickerSlotSuggestionsResult['renderSuggestDialog'];
}

export function useSystemPickerConfig({
  open,
  mode,
  processId,
  recordingId,
  startEventNodeId,
  draftProcessId,
  requestContext,
  selectedIds,
  pendingSuggestions,
  allowCreate
}: UseSystemPickerConfigArgs): UseSystemPickerConfigResult {
  const { t } = useTranslation();
  const { can } = usePermissions();
  const { data: systems = [], isLoading } = useSystems();
  const { data: suggestions, isLoading: isLoadingSuggestions } = useSystemPickerSuggestions({
    processId,
    recordingId,
    enabled: open
  });

  const slot = usePickerSlotSuggestions({
    mode,
    selectedIds,
    sourceRecordingId: requestContext?.sourceStepRecordingId ?? startEventNodeId,
    // Cache-seed hint only; a no-op unless the process is cached.
    seedProcessId: processId,
    draftProcessId,
    SuggestionDialog: SystemSuggestionDialog,
    allowCreate: !!allowCreate && can('System', 'create'),
    CreateDialog: SystemCreateDialog
  });

  // Synthesize System items for pending suggestions so they show up alongside
  // real systems — same checkbox row, same search match path. The prefixed
  // `_id` keeps them disambiguated from real systems when the picker hands
  // selected ids back to the consumer.
  const pendingItems = useMemo(
    () => buildPendingSystemItems(pendingSuggestions),
    [pendingSuggestions]
  );

  const systemList = useMemo<System[]>(
    () => [...(systems as System[]), ...pendingItems],
    [systems, pendingItems]
  );

  const rowMetaById = useMemo(() => buildRowMetaIndex(suggestions), [suggestions]);

  const config = useMemo<EntityPickerConfig<System>>(() => {
    const baseCopy = buildEntityPickerCopy(t, 'system', slot.mode);
    return {
      headerIcon: <ENTITY_ICONS.system className="h-4 w-4" />,
      searchKeys: SEARCH_KEYS,
      matchInField: {
        primaryField: 'name',
        fieldLabels: {
          description: t('entityPicker.common.fields.description'),
          category: t('entityPicker.common.fields.category')
        }
      },
      facet: { field: 'category', allLabel: t('entityPicker.common.allFacet') },
      getRowMeta: (item) => rowMetaById.get(item._id) ?? {},
      getChipName: (item) => item.name ?? '',
      renderAllListTrailingTag: (item) =>
        item.category ? (
          <Badge variant="outline" className="text-xs">
            {item.category}
          </Badge>
        ) : null,
      renderRow: (item, ctx) => (
        <SystemCard
          system={item}
          variant="with-category"
          itemVariant="default"
          size="xs"
          titleSlot={
            <EntityPickerHighlightedText
              text={item.name ?? ''}
              field="name"
              matches={ctx.matches}
            />
          }
        />
      ),
      bucketing: {
        isLoading: isLoadingSuggestions,
        hasBuckets: (candidates) => shouldShowBucketView(suggestions, candidates, pendingItems),
        renderEmptyQueryView: ({ items, selectedIds: draft, toggleId }) => (
          <SystemPickerBuckets
            systems={items}
            pendingItems={pendingItems}
            selectedIds={draft}
            onToggle={toggleId}
            mode={mode}
            suggestions={suggestions}
          />
        )
      },
      copy: {
        ...baseCopy,
        resultCountLabel: (count) => t('entityPicker.system.resultCount', { count })
      }
    };
  }, [t, suggestions, isLoadingSuggestions, rowMetaById, mode, pendingItems, slot.mode]);

  return {
    items: systemList,
    isLoading,
    mergedSelectedIds: selectedIds,
    config,
    suggestNew: slot.suggestNew,
    slotMode: slot.mode,
    renderSuggestDialog: slot.renderSuggestDialog
  };
}
